import '../../i18n/autoTranslate';
import React, { useMemo, useState } from 'react';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import Icon from '../../../components/AppIcon';

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const safeStr = (v, d = '') => (typeof v === 'string' && v.trim() ? v.trim() : d);

const categoryMeta = {
  all: { label: 'All', icon: 'Award' },
  milestones: { label: 'Milestones', icon: 'Flag' },
  performance: { label: 'Performance', icon: 'TrendingUp' },
  consistency: { label: 'Consistency', icon: 'Calendar' },
  diversity: { label: 'Diversity', icon: 'Layers' },
};

const rarityStyle = (rarity) => {
  switch (rarity) {
    case 'epic':
      return 'border-violet-400/25 bg-violet-400/10';
    case 'rare':
      return 'border-sky-400/25 bg-sky-400/10';
    case 'common':
    default:
      return 'border-white/12 bg-white/[0.03]';
  }
};

const AchievementGallery = ({ achievements = [] }) => {
  const [category, setCategory] = useState('all');

  const normalized = useMemo(() => {
    const base = Array.isArray(achievements) ? achievements : [];
    return base.map((a, idx) => ({
      id: a.id ?? `ach_${idx}`,
      title: safeStr(a.title, 'Achievement'),
      description: safeStr(a.description, ''),
      icon: safeStr(a.icon, 'Award'),
      category: safeStr(a.category, 'milestones'),
      rarity: safeStr(a.rarity, 'common'),
      progress: Math.max(0, Math.min(100, Math.round(safeNum(a.progress, 0)))),
      unlocked: Boolean(a.unlocked),
      points: safeNum(a.points, 0),
    }));
  }, [achievements]);

  const filtered = useMemo(() => {
    if (category === 'all') return normalized;
    return normalized.filter((a) => a.category === category);
  }, [normalized, category]);

  const stats = useMemo(() => {
    const earned = normalized.filter((a) => a.unlocked).length;
    const avg = normalized.length
      ? Math.round((normalized.reduce((s, a) => s + a.progress, 0) / normalized.length) * 10) / 10
      : 0;
    const totalPoints = normalized.reduce((s, a) => s + (a.unlocked ? (a.points || 0) : 0), 0);
    return { earned, avg, totalPoints };
  }, [normalized]);

  const categories = useMemo(() => {
    const keys = ['all', 'milestones', 'performance', 'consistency', 'diversity'];
    const counts = normalized.reduce((acc, a) => {
      acc[a.category] = (acc[a.category] || 0) + 1;
      return acc;
    }, {});
    return keys.map((k) => ({
      value: k,
      label: categoryMeta[k]?.label || k,
      icon: categoryMeta[k]?.icon || 'Award',
      count: k === 'all' ? normalized.length : (counts[k] || 0),
    }));
  }, [normalized]);

  return (
    <SpotlightCard
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]"
      spotlightColor="rgba(245,158,11,0.12)"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-[320px] w-[320px] rounded-full bg-amber-300/12 blur-[90px]" />
        <div className="absolute -bottom-24 -right-24 h-[320px] w-[320px] rounded-full bg-violet-400/10 blur-[90px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
      </div>

      <div className="relative z-10 p-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-white">Achievements</div>
            <div className="text-xs text-white/55 mt-1">Progress that keeps you accountable (and motivated).</div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {categories.map((c) => {
              const active = category === c.value;
              return (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  className={[
                    "inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition",
                    active ? "border-white/14 bg-white/[0.10] text-white" : "border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/[0.06]",
                  ].join(" ")}
                >
                  <Icon name={c.icon} size={14} className={active ? "text-white" : "text-white/70"} />
                  {c.label}
                  <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70">{c.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.length ? (
            filtered.map((a) => (
              <div
                key={a.id}
                className={[
                  "relative overflow-hidden rounded-3xl border p-5",
                  a.unlocked ? "border-white/14 bg-white/[0.04]" : rarityStyle(a.rarity),
                ].join(" ")}
              >
                <div className="pointer-events-none absolute inset-0">
                  <div
                    className="absolute -top-20 -right-20 h-40 w-40 rounded-full blur-[70px]"
                    style={{
                      background:
                        a.rarity === 'epic'
                          ? 'rgba(99,102,241,0.18)'
                          : a.rarity === 'rare'
                          ? 'rgba(56,189,248,0.16)'
                          : 'rgba(255,255,255,0.06)',
                    }}
                  />
                </div>

                <div className="relative z-10 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                        <Icon name={a.icon} size={16} className={a.unlocked ? "text-emerald-200" : "text-white/70"} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{a.title}</div>
                        <div className="text-xs text-white/55">{a.description}</div>
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0">
                    <div
                      className="h-12 w-12 rounded-full border border-white/10 grid place-items-center"
                      style={{
                        background: `conic-gradient(rgba(56,189,248,0.85) ${a.progress * 3.6}deg, rgba(255,255,255,0.10) 0deg)`,
                      }}
                    >
                      <div className="h-9 w-9 rounded-full bg-[#070a10]/80 grid place-items-center text-[11px] font-semibold text-white/85">
                        {a.progress}%
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 h-2 rounded-full bg-white/8 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-violet-400/70 via-sky-400/70 to-emerald-400/70"
                    style={{ width: `${a.progress}%` }}
                  />
                </div>

                <div className="mt-3 flex items-center justify-between text-[11px] text-white/55">
                  <span className="capitalize">{a.rarity}</span>
                  {a.unlocked ? (
                    <span className="inline-flex items-center gap-1 text-emerald-200">
                      <Icon name="BadgeCheck" size={14} />
                      Unlocked
                    </span>
                  ) : (
                    <span>In progress</span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center text-sm text-white/55 py-10">
              No achievements in this category yet.
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-semibold text-white">{stats.earned}</div>
              <div className="text-sm text-white/55">Unlocked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-white">{Math.max(0, normalized.length - stats.earned)}</div>
              <div className="text-sm text-white/55">Remaining</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-emerald-200">{stats.avg}%</div>
              <div className="text-sm text-white/55">Avg progress</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-white">{stats.totalPoints}</div>
              <div className="text-sm text-white/55">Points</div>
            </div>
          </div>
        </div>
      </div>
    </SpotlightCard>
  );
};

export default AchievementGallery;
